import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { PROPERTY_TYPES, USE_CASES, OFFER_TYPES, CONTACT_ROLES, scoreLabel } from "@/lib/constants";
import { useSeo } from "@/hooks/useSeo";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ChevronLeft, ChevronRight, Upload, X } from "lucide-react";

const STEPS = [
  "Publier mon lieu",
  "Accès",
  "Technique",
  "Élec. / Sécurité",
  "Réglementaire",
  "Usages",
  "Économique",
  "Médias",
  "Contact",
];

function TextField({ label, value, onChange, type = "text", step, testId }) {
  return (
    <div>
      <div className="label-mono mb-2">{label}</div>
      <input
        type={type}
        step={step}
        value={value ?? ""}
        onChange={(e) => onChange(type === "number" ? Number(e.target.value) : e.target.value)}
        data-testid={testId}
        className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2.5 text-sm focus:outline-none focus:border-amber-500"
      />
    </div>
  );
}
function Bool({ label, value, onChange, testId }) {
  return (
    <label className="flex items-center gap-3 cursor-pointer py-1.5">
      <div onClick={() => onChange(!value)} data-testid={testId} className={`w-10 h-6 border transition-colors flex items-center px-0.5 ${value ? "border-amber-500 bg-amber-500/20" : "border-zinc-700 bg-zinc-900"}`}>
        <div className={`w-4 h-4 transition-transform ${value ? "translate-x-4 bg-amber-500" : "bg-zinc-600"}`} />
      </div>
      <span className="text-sm text-zinc-200">{label}</span>
    </label>
  );
}
function Sel({ label, value, onChange, options, testId }) {
  return (
    <div>
      <div className="label-mono mb-2">{label}</div>
      <select value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2.5 text-sm focus:outline-none focus:border-amber-500">
        <option value="">—</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

const initial = {
  title: "", property_type: "niveau_-1", address_full: "", address_display: "quartier", city: "", postal_code: "", arrondissement: "",
  availability: "immediate", surface_total: 0, surface_exploitable: 0, nb_niveaux: 1,
  access_independent: false, access_common: false, access_commerce: false, passage_width: 0, stairs_width: 0,
  has_elevator: false, has_freight_elevator: false, pmr_access: false, delivery_possible: false, vehicle_access: false,
  access_247: false, schedule_constraints: "",
  ceiling_height_min: 0, ceiling_height_avg: 0, has_pillars: false, floor_type: "", wall_type: "", condition: "brut",
  humidity: false, infiltrations: false, ventilation: false, extraction: false, heating: false, ac: false,
  water_supply: false, wastewater: false, toilets: false, shower: false, kitchen: false, internet: false,
  has_tgbt: false, power_kva: 0, triphase: false, dedicated_panel: false, electrical_compliance: false,
  emergency_lighting: false, fire_alarm: false, fire_detection: false, smoke_extraction: false, extinguishers: false,
  nb_exits: 1, max_exit_distance: 0, backup_generator: false, video_surveillance: false, access_control: false,
  is_erp: false, erp_category: "", erp_type: "", last_safety_commission: "", destination_change_possible: false,
  coproprop_regulation_available: false, commercial_authorized: false, noise_authorized: false, public_reception_authorized: false,
  storage_authorized: false, restauration_authorized: false, alcohol_authorized: false, works_possible: false,
  facade_piercing_possible: false, landlord_auth_needed: false,
  compatible_uses: {}, preferred_uses: [], preferred_uses_note: "",
  offer_type: "location", monthly_rent: 0, monthly_charges: 0, property_tax: 0, deposit: 0, pas_de_porte: 0,
  fees: 0, duration_months: 0, rent_free_period: false, tenant_works: false, owner_works_contribution: false, sale_price: 0,
  photos: [], documents: [], video_url: "", virtual_tour_url: "",
  contact_name: "", contact_company: "", contact_role: "proprietaire", contact_email: "", contact_phone: "",
  preferred_contact: "email", exclusive_mandate: false, publication_authorized: true,
  description: "",
};

export default function SubmitListing() {
  useSeo({
    title: "Proposer un sous-sol",
    description: "Publiez votre sous-sol, cave, parking ou local atypique sur R-1.immo. Formulaire guidé en 9 étapes : identification, accès, technique, ERP, usages, conditions économiques. Score d'exploitabilité calculé automatiquement.",
    path: "/deposer",
  });
  const nav = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ ...initial, contact_name: user?.name || "", contact_email: user?.email || "" });
  const [uploading, setUploading] = useState(false);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  // Live score
  const liveScore = computeLive(form);
  const sInfo = scoreLabel(liveScore);

  const uploadFiles = async (files, kind) => {
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const endpoint = user ? "/upload" : "/upload/anonymous";
        const { data } = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push({ path: data.path, name: file.name, type: kind });
      }
      if (kind === "photo") {
        set({ photos: [...form.photos, ...uploaded.map((u) => u.path)] });
      } else {
        set({ documents: [...form.documents, ...uploaded] });
      }
      toast.success(`${uploaded.length} fichier(s) uploadé(s)`);
    } catch (e) {
      toast.error("Échec upload");
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = (p) => set({ photos: form.photos.filter((x) => x !== p) });
  const removeDoc = (d) => set({ documents: form.documents.filter((x) => x !== d) });

  const submit = async () => {
    if (!form.contact_email) {
      toast.error("Un email valide est requis pour valider votre annonce");
      setStep(8);
      return;
    }
    try {
      if (user) {
        await api.post("/listings", form);
        toast.success("Annonce soumise — en attente de validation");
        nav("/dashboard");
      } else {
        const { data } = await api.post("/listings/anonymous", {
          email: form.contact_email,
          name: form.contact_name,
          listing: form,
        });
        if (data.email_sent) {
          nav(`/verifier?sent=${encodeURIComponent(form.contact_email)}`);
        } else {
          toast.error("Annonce enregistrée mais l'email n'a pas pu être envoyé. Contactez-nous.");
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la soumission");
    }
  };

  const canNext = () => {
    if (step === 0) return form.title && form.city && form.address_full;
    return true;
  };

  return (
    <div className="px-6 md:px-12 lg:px-16 py-12">
      {!user && (
        <div className="mb-6 border border-amber-500/30 bg-amber-500/5 p-4 flex items-start gap-3">
          <div className="label-mono text-amber-500 shrink-0 mt-0.5">§ INFO</div>
          <p className="text-sm text-zinc-300 leading-relaxed">
            Pas besoin de créer un compte maintenant. Remplissez votre annonce jusqu'au bout — vous validerez votre email et créerez votre mot de passe en un clic à la fin.
          </p>
        </div>
      )}
      {/* Progress */}
      <div className="mb-12">
        <div className="flex items-center justify-between mb-3">
          <div className="label-mono text-amber-500">§ étape {step + 1} / {STEPS.length}</div>
          <div className={`font-mono-tech text-sm ${sInfo.color}`} data-testid="live-score">[ SCORE : {liveScore} / 100 ]</div>
        </div>
        <div className="grid grid-cols-9 gap-1">
          {STEPS.map((s, i) => (
            <div key={s} className={`h-1 ${i <= step ? "bg-amber-500" : "bg-zinc-800"}`} />
          ))}
        </div>
        <div className="flex items-center justify-between mt-3">
          <div className="font-display text-3xl">{STEPS[step]}</div>
          <div className="hidden md:flex gap-3 label-mono text-zinc-500">
            {STEPS.map((s, i) => (
              <button key={s} onClick={() => setStep(i)} className={i === step ? "text-amber-500" : ""}>{String(i + 1).padStart(2, "0")}</button>
            ))}
          </div>
        </div>
      </div>

      <div className="border border-zinc-800 bg-zinc-950 p-6 md:p-10 min-h-[400px]">
        {step === 0 && (
          <div className="grid md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <TextField label="Titre de l'annonce" value={form.title} onChange={(v) => set({ title: v })} testId="step0-title" />
            </div>
            <Sel label="Type de bien" value={form.property_type} onChange={(v) => set({ property_type: v })} options={PROPERTY_TYPES} testId="step0-property-type" />
            <TextField label="Disponibilité (immédiate, sous 3 mois…)" value={form.availability} onChange={(v) => set({ availability: v })} />
            <div className="md:col-span-2">
              <TextField label="Adresse complète (privée)" value={form.address_full} onChange={(v) => set({ address_full: v })} testId="step0-address" />
            </div>
            <TextField label="Ville" value={form.city} onChange={(v) => set({ city: v })} testId="step0-city" />
            <TextField label="Code postal" value={form.postal_code} onChange={(v) => set({ postal_code: v })} />
            <TextField label="Arrondissement / Quartier" value={form.arrondissement} onChange={(v) => set({ arrondissement: v })} />
            <Sel label="Affichage public de l'adresse" value={form.address_display} onChange={(v) => set({ address_display: v })} options={[
              { value: "exact", label: "Adresse exacte" }, { value: "rue", label: "Rue seulement" },
              { value: "quartier", label: "Quartier seulement" }, { value: "ville", label: "Ville uniquement" },
            ]} />
            <TextField label="Surface totale (m²)" type="number" value={form.surface_total} onChange={(v) => set({ surface_total: v })} testId="step0-surface" />
            <TextField label="Surface exploitable (m²)" type="number" value={form.surface_exploitable} onChange={(v) => set({ surface_exploitable: v })} />
            <TextField label="Nombre de niveaux en sous-sol" type="number" value={form.nb_niveaux} onChange={(v) => set({ nb_niveaux: v })} />
            <div className="md:col-span-2">
              <div className="label-mono mb-2">Description libre</div>
              <textarea rows="4" value={form.description} onChange={(e) => set({ description: e.target.value })} className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2.5 text-sm focus:outline-none focus:border-amber-500" />
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="grid md:grid-cols-2 gap-6">
            <Bool label="Accès indépendant depuis la rue" value={form.access_independent} onChange={(v) => set({ access_independent: v })} testId="acc-indep" />
            <Bool label="Accès via parties communes" value={form.access_common} onChange={(v) => set({ access_common: v })} />
            <Bool label="Accès via commerce / immeuble" value={form.access_commerce} onChange={(v) => set({ access_commerce: v })} />
            <TextField label="Largeur passage min. (m)" type="number" step="0.1" value={form.passage_width} onChange={(v) => set({ passage_width: v })} />
            <TextField label="Largeur escalier (m)" type="number" step="0.1" value={form.stairs_width} onChange={(v) => set({ stairs_width: v })} />
            <Bool label="Ascenseur" value={form.has_elevator} onChange={(v) => set({ has_elevator: v })} />
            <Bool label="Monte-charge" value={form.has_freight_elevator} onChange={(v) => set({ has_freight_elevator: v })} />
            <Bool label="Accès PMR" value={form.pmr_access} onChange={(v) => set({ pmr_access: v })} />
            <Bool label="Livraison possible" value={form.delivery_possible} onChange={(v) => set({ delivery_possible: v })} />
            <Bool label="Accès véhicule" value={form.vehicle_access} onChange={(v) => set({ vehicle_access: v })} />
            <Bool label="Accès 24h/24 7j/7" value={form.access_247} onChange={(v) => set({ access_247: v })} />
            <div className="md:col-span-2">
              <TextField label="Contraintes horaires (copropriété, voisinage…)" value={form.schedule_constraints} onChange={(v) => set({ schedule_constraints: v })} />
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="grid md:grid-cols-2 gap-6">
            <TextField label="Hauteur sous plafond min. (m)" type="number" step="0.1" value={form.ceiling_height_min} onChange={(v) => set({ ceiling_height_min: v })} testId="height-min" />
            <TextField label="Hauteur sous plafond moy. (m)" type="number" step="0.1" value={form.ceiling_height_avg} onChange={(v) => set({ ceiling_height_avg: v })} />
            <Bool label="Présence de poteaux" value={form.has_pillars} onChange={(v) => set({ has_pillars: v })} />
            <Sel label="Sol" value={form.floor_type} onChange={(v) => set({ floor_type: v })} options={[
              { value: "beton", label: "Béton" }, { value: "carrelage", label: "Carrelage" },
              { value: "terre", label: "Terre battue" }, { value: "parquet", label: "Parquet" }, { value: "autre", label: "Autre" },
            ]} />
            <Sel label="Murs" value={form.wall_type} onChange={(v) => set({ wall_type: v })} options={[
              { value: "pierre", label: "Pierre" }, { value: "beton", label: "Béton" },
              { value: "brique", label: "Brique" }, { value: "placo", label: "Placo" }, { value: "brut", label: "Brut" },
            ]} />
            <Sel label="État général" value={form.condition} onChange={(v) => set({ condition: v })} options={[
              { value: "brut", label: "Brut" }, { value: "a_rafraichir", label: "À rafraîchir" },
              { value: "exploitable", label: "Exploitable" }, { value: "renove", label: "Rénové" },
            ]} />
            <Bool label="Humidité constatée" value={form.humidity} onChange={(v) => set({ humidity: v })} />
            <Bool label="Infiltrations" value={form.infiltrations} onChange={(v) => set({ infiltrations: v })} />
            <Bool label="Ventilation existante" value={form.ventilation} onChange={(v) => set({ ventilation: v })} testId="ventilation" />
            <Bool label="Extraction existante" value={form.extraction} onChange={(v) => set({ extraction: v })} />
            <Bool label="Chauffage" value={form.heating} onChange={(v) => set({ heating: v })} />
            <Bool label="Climatisation" value={form.ac} onChange={(v) => set({ ac: v })} />
            <Bool label="Arrivée d'eau" value={form.water_supply} onChange={(v) => set({ water_supply: v })} />
            <Bool label="Évacuation eaux usées" value={form.wastewater} onChange={(v) => set({ wastewater: v })} />
            <Bool label="Sanitaires" value={form.toilets} onChange={(v) => set({ toilets: v })} />
            <Bool label="Douche" value={form.shower} onChange={(v) => set({ shower: v })} />
            <Bool label="Cuisine / point d'eau" value={form.kitchen} onChange={(v) => set({ kitchen: v })} />
            <Bool label="Internet / fibre" value={form.internet} onChange={(v) => set({ internet: v })} />
          </div>
        )}

        {step === 3 && (
          <div className="grid md:grid-cols-2 gap-6">
            <Bool label="TGBT présent" value={form.has_tgbt} onChange={(v) => set({ has_tgbt: v })} />
            <TextField label="Puissance électrique (kVA)" type="number" value={form.power_kva} onChange={(v) => set({ power_kva: v })} />
            <Bool label="Triphasé" value={form.triphase} onChange={(v) => set({ triphase: v })} />
            <Bool label="Tableau électrique dédié" value={form.dedicated_panel} onChange={(v) => set({ dedicated_panel: v })} />
            <Bool label="Conformité électrique connue" value={form.electrical_compliance} onChange={(v) => set({ electrical_compliance: v })} />
            <Bool label="Éclairage de sécurité" value={form.emergency_lighting} onChange={(v) => set({ emergency_lighting: v })} />
            <Bool label="Alarme incendie" value={form.fire_alarm} onChange={(v) => set({ fire_alarm: v })} />
            <Bool label="Détection incendie" value={form.fire_detection} onChange={(v) => set({ fire_detection: v })} />
            <Bool label="Désenfumage" value={form.smoke_extraction} onChange={(v) => set({ smoke_extraction: v })} />
            <Bool label="Extincteurs" value={form.extinguishers} onChange={(v) => set({ extinguishers: v })} />
            <TextField label="Nombre d'issues de secours" type="number" value={form.nb_exits} onChange={(v) => set({ nb_exits: v })} />
            <TextField label="Distance max. à une sortie (m)" type="number" value={form.max_exit_distance} onChange={(v) => set({ max_exit_distance: v })} />
            <Bool label="Groupe électrogène / secours" value={form.backup_generator} onChange={(v) => set({ backup_generator: v })} />
            <Bool label="Vidéosurveillance" value={form.video_surveillance} onChange={(v) => set({ video_surveillance: v })} />
            <Bool label="Contrôle d'accès" value={form.access_control} onChange={(v) => set({ access_control: v })} />
          </div>
        )}

        {step === 4 && (
          <div className="grid md:grid-cols-2 gap-6">
            <Bool label="Le local est déjà ERP" value={form.is_erp} onChange={(v) => set({ is_erp: v })} testId="is-erp" />
            <TextField label="Catégorie ERP" value={form.erp_category} onChange={(v) => set({ erp_category: v })} />
            <TextField label="Type ERP" value={form.erp_type} onChange={(v) => set({ erp_type: v })} />
            <TextField label="Dernière commission de sécurité" value={form.last_safety_commission} onChange={(v) => set({ last_safety_commission: v })} />
            <Bool label="Changement de destination possible" value={form.destination_change_possible} onChange={(v) => set({ destination_change_possible: v })} />
            <Bool label="Règlement copropriété disponible" value={form.coproprop_regulation_available} onChange={(v) => set({ coproprop_regulation_available: v })} />
            <Bool label="Activité commerciale autorisée" value={form.commercial_authorized} onChange={(v) => set({ commercial_authorized: v })} />
            <Bool label="Nuisance sonore autorisée" value={form.noise_authorized} onChange={(v) => set({ noise_authorized: v })} />
            <Bool label="Réception du public autorisée" value={form.public_reception_authorized} onChange={(v) => set({ public_reception_authorized: v })} />
            <Bool label="Stockage autorisé" value={form.storage_authorized} onChange={(v) => set({ storage_authorized: v })} />
            <Bool label="Restauration autorisée" value={form.restauration_authorized} onChange={(v) => set({ restauration_authorized: v })} />
            <Bool label="Alcool autorisé" value={form.alcohol_authorized} onChange={(v) => set({ alcohol_authorized: v })} />
            <Bool label="Travaux possibles" value={form.works_possible} onChange={(v) => set({ works_possible: v })} />
            <Bool label="Percement façade / extraction possible" value={form.facade_piercing_possible} onChange={(v) => set({ facade_piercing_possible: v })} />
            <Bool label="Autorisation bailleur / copro nécessaire" value={form.landlord_auth_needed} onChange={(v) => set({ landlord_auth_needed: v })} />
          </div>
        )}

        {step === 5 && (
          <div className="space-y-8">
            <div>
              <div className="label-mono mb-3 text-amber-500">§ ce que vous préconisez ou autorisez</div>
              <p className="text-sm text-zinc-400 mb-4">Cochez les usages que vous <strong>souhaitez</strong> voir prendre place dans votre lieu. Ce sera mis en avant pour les exploitants.</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {USE_CASES.map((u) => {
                  const active = form.preferred_uses.includes(u.value);
                  return (
                    <button
                      key={u.value}
                      type="button"
                      onClick={() => set({ preferred_uses: active ? form.preferred_uses.filter((x) => x !== u.value) : [...form.preferred_uses, u.value] })}
                      data-testid={`preferred-${u.value}`}
                      className={`text-left px-3 py-2.5 text-sm border transition-colors ${active ? "border-amber-500 bg-amber-500/10 text-amber-500" : "border-zinc-800 text-zinc-300 hover:border-zinc-600"}`}
                    >
                      {active ? "✓ " : "+ "}{u.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <div className="label-mono mb-2">Précision libre (optionnel)</div>
              <textarea
                rows="3"
                value={form.preferred_uses_note}
                onChange={(e) => set({ preferred_uses_note: e.target.value })}
                placeholder="Ex. Je privilégie un usage calme, pas de nuisances nocturnes. Ouvert à des projets créatifs."
                data-testid="preferred-uses-note"
                className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2.5 text-sm focus:outline-none focus:border-amber-500"
              />
            </div>

            <div className="border-t border-zinc-800 pt-6">
              <div className="label-mono mb-3">§ compatibilité détaillée par usage</div>
              <p className="text-sm text-zinc-400 mb-4">Pour chaque usage, indiquez la compatibilité technique/réglementaire de votre lieu (peut différer de vos préférences).</p>
              <div className="grid md:grid-cols-2 gap-px bg-zinc-800 border border-zinc-800">
                {USE_CASES.map((u) => (
                  <div key={u.value} className="bg-zinc-950 p-3 flex items-center justify-between gap-3">
                    <div className="text-sm">{u.label}</div>
                    <select
                      value={form.compatible_uses[u.value] || ""}
                      onChange={(e) => set({ compatible_uses: { ...form.compatible_uses, [u.value]: e.target.value } })}
                      className="bg-zinc-900 border border-zinc-800 px-2 py-1 text-xs label-mono focus:outline-none focus:border-amber-500"
                      data-testid={`use-${u.value}`}
                    >
                      <option value="">—</option>
                      <option value="autorise">AUTORISÉ</option>
                      <option value="peut-etre">PEUT-ÊTRE</option>
                      <option value="non">NON</option>
                      <option value="a_verifier">À VÉRIFIER</option>
                    </select>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {step === 6 && (
          <div className="grid md:grid-cols-2 gap-6">
            <Sel label="Type d'offre" value={form.offer_type} onChange={(v) => set({ offer_type: v })} options={OFFER_TYPES} testId="offer-type" />
            <TextField label="Loyer mensuel HT (€)" type="number" value={form.monthly_rent} onChange={(v) => set({ monthly_rent: v })} testId="monthly-rent" />
            <TextField label="Charges mensuelles (€)" type="number" value={form.monthly_charges} onChange={(v) => set({ monthly_charges: v })} />
            <TextField label="Taxe foncière annuelle (€)" type="number" value={form.property_tax} onChange={(v) => set({ property_tax: v })} />
            <TextField label="Dépôt de garantie (€)" type="number" value={form.deposit} onChange={(v) => set({ deposit: v })} />
            <TextField label="Pas-de-porte (€)" type="number" value={form.pas_de_porte} onChange={(v) => set({ pas_de_porte: v })} />
            <TextField label="Honoraires (€)" type="number" value={form.fees} onChange={(v) => set({ fees: v })} />
            <TextField label="Durée souhaitée (mois)" type="number" value={form.duration_months} onChange={(v) => set({ duration_months: v })} />
            <TextField label="Prix de vente (€)" type="number" value={form.sale_price} onChange={(v) => set({ sale_price: v })} />
            <Bool label="Franchise de loyer possible" value={form.rent_free_period} onChange={(v) => set({ rent_free_period: v })} />
            <Bool label="Travaux à la charge du preneur" value={form.tenant_works} onChange={(v) => set({ tenant_works: v })} />
            <Bool label="Participation propriétaire aux travaux" value={form.owner_works_contribution} onChange={(v) => set({ owner_works_contribution: v })} />
          </div>
        )}

        {step === 7 && (
          <div className="space-y-8">
            <div>
              <div className="label-mono mb-3">Photos du lieu</div>
              <label className="border-2 border-dashed border-zinc-800 hover:border-amber-500/50 p-8 flex flex-col items-center gap-3 cursor-pointer transition-colors block">
                <Upload size={24} className="text-zinc-500" />
                <div className="label-mono">Cliquer pour ajouter des photos</div>
                <input type="file" multiple accept="image/*" onChange={(e) => uploadFiles(Array.from(e.target.files), "photo")} className="hidden" data-testid="upload-photos" />
              </label>
              {uploading && <div className="mt-2 label-mono text-amber-500">Upload en cours…</div>}
              {form.photos.length > 0 && (
                <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mt-4">
                  {form.photos.map((p) => (
                    <div key={p} className="relative aspect-square">
                      <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`} alt="" className="w-full h-full object-cover border border-zinc-800" />
                      <button onClick={() => removePhoto(p)} className="absolute top-1 right-1 bg-zinc-950/80 p-1 hover:bg-red-500"><X size={12} /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <div className="label-mono mb-3">Documents (plans, diagnostics, bail…)</div>
              <label className="border-2 border-dashed border-zinc-800 hover:border-amber-500/50 p-6 flex flex-col items-center gap-2 cursor-pointer transition-colors block">
                <Upload size={20} className="text-zinc-500" />
                <div className="label-mono">Ajouter des documents PDF</div>
                <input type="file" multiple accept=".pdf,.doc,.docx" onChange={(e) => uploadFiles(Array.from(e.target.files), "doc")} className="hidden" data-testid="upload-docs" />
              </label>
              {form.documents.length > 0 && (
                <ul className="mt-3 border border-zinc-800 divide-y divide-zinc-800">
                  {form.documents.map((d) => (
                    <li key={d.path} className="p-2 flex justify-between items-center">
                      <span className="text-sm">{d.name}</span>
                      <button onClick={() => removeDoc(d)}><X size={14} className="text-zinc-500 hover:text-red-500" /></button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <TextField label="URL Vidéo" value={form.video_url} onChange={(v) => set({ video_url: v })} />
              <TextField label="URL Visite virtuelle" value={form.virtual_tour_url} onChange={(v) => set({ virtual_tour_url: v })} />
            </div>
          </div>
        )}

        {step === 8 && (
          <div className="grid md:grid-cols-2 gap-6">
            <TextField label="Nom" value={form.contact_name} onChange={(v) => set({ contact_name: v })} />
            <TextField label="Société" value={form.contact_company} onChange={(v) => set({ contact_company: v })} />
            <Sel label="Rôle" value={form.contact_role} onChange={(v) => set({ contact_role: v })} options={CONTACT_ROLES} />
            <TextField label="Email" value={form.contact_email} onChange={(v) => set({ contact_email: v })} type="email" />
            <TextField label="Téléphone" value={form.contact_phone} onChange={(v) => set({ contact_phone: v })} />
            <Sel label="Préférence de contact" value={form.preferred_contact} onChange={(v) => set({ preferred_contact: v })} options={[
              { value: "email", label: "Email" }, { value: "phone", label: "Téléphone" }, { value: "both", label: "Les deux" }
            ]} />
            <Bool label="Mandat exclusif" value={form.exclusive_mandate} onChange={(v) => set({ exclusive_mandate: v })} />
            <Bool label="J'autorise la publication de cette annonce" value={form.publication_authorized} onChange={(v) => set({ publication_authorized: v })} testId="publish-auth" />
          </div>
        )}
      </div>

      {/* Nav */}
      <div className="flex justify-between mt-6">
        <button
          disabled={step === 0}
          onClick={() => setStep(step - 1)}
          data-testid="prev-step-btn"
          className="flex items-center gap-2 border border-zinc-800 px-6 py-3 text-xs uppercase tracking-widest disabled:opacity-30 hover:border-zinc-600"
        >
          <ChevronLeft size={14} /> Précédent
        </button>
        {step === STEPS.length - 1 ? (
          <button onClick={submit} disabled={!form.publication_authorized || !form.contact_email} data-testid="submit-listing-btn" className="flex items-center gap-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-30 text-black px-8 py-3 text-xs uppercase tracking-widest">
            {user ? "Soumettre l'annonce" : "Envoyer & recevoir le lien de validation"}
          </button>
        ) : (
          <button disabled={!canNext()} onClick={() => setStep(step + 1)} data-testid="next-step-btn" className="flex items-center gap-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-30 text-black px-6 py-3 text-xs uppercase tracking-widest">
            Suivant <ChevronRight size={14} />
          </button>
        )}
      </div>
    </div>
  );
}

function computeLive(l) {
  let s = 0;
  if (l.ceiling_height_min >= 2.5) s += 15; else if (l.ceiling_height_min >= 2.2) s += 10; else if (l.ceiling_height_min >= 1.8) s += 5;
  if (l.access_independent) s += 12;
  if (l.is_erp) s += 15; else if (l.public_reception_authorized) s += 8;
  if (l.ventilation) s += 8;
  if (l.extraction) s += 6;
  if (l.nb_exits >= 2) s += 8; else if (l.nb_exits >= 1) s += 4;
  if (l.has_tgbt || l.power_kva > 12) s += 10;
  if (l.toilets) s += 6;
  if (!l.humidity && !l.infiltrations) s += 10; else if (!l.infiltrations) s += 5;
  if (l.works_possible) s += 6;
  if (l.delivery_possible) s += 4;
  return Math.min(s, 100);
}
